// Reusable Matrix component for the Management View (PRODOPSAI-389).
//
// One Matrix renders a teams-by-time table with:
//   - threshold-coloured cells (via the shared `classify` classifier)
//   - an optional "org-wide" first row with navy chrome and white sparkline
//   - team rows sorted best-to-worst by the most recent column
//   - a 70x22 SVG sparkline trend column (omitted for rows with any null)
//   - a Δ vs last column with favourable-direction colouring
//   - cell-click and column-header-click callbacks
//
// PURE PRESENTATION: no fetching, no async work, no side effects beyond
// invoking the optional callbacks supplied via the spec. Used 11+ times
// across Phase 1, 2, and 3 of the Management View — keep changes here
// surgical and additive.
//
// Architecture decision: the column count is derived from
// `spec.columnLabels.length`; the mockup hard-coded 6 but Phase 0 must
// support 3 / 6 / 12 / 13 etc. Rows whose `values.length` differs from
// `columnLabels.length` are still rendered — the surplus/missing positions
// fall through to the no-data renderer, so the component never throws on
// a mismatched contract.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { IMatrixRow, IMatrixSpec } from '@skystone/shared';
import {
  cellBackgroundClass,
  classifyCellTone,
  describeDelta,
  formatValue,
  orgCellBackgroundClass,
  sortRowsByLatest,
  sparklineStroke,
  type DeltaDescriptor,
  type MatrixCellTone,
} from './matrix-helpers';
import { MatrixDelta } from './MatrixDelta';
import { MatrixSparkline } from './MatrixSparkline';

// ---------------------------------------------------------------------------
// Module-level helper components — defined outside Matrix() to satisfy
// SonarCloud S7721 (no helper components inside the render body).
// ---------------------------------------------------------------------------

interface CellProps {
  value: number | null;
  tone: MatrixCellTone;
  format: IMatrixSpec['format'];
  org: boolean;
  noDataReason?: string;
  onActivate?: () => void;
  hasOnClick: boolean;
}

const CELL_BASE_CLASSES =
  'px-1.5 py-2 text-center font-semibold tabular-nums border-b border-r border-[var(--color-border)] last:border-r-0';

function noDataCell(noDataReason: string | undefined, org: boolean): ReactNode {
  return (
    <td
      className={cn(
        CELL_BASE_CLASSES,
        'italic font-medium',
        org ? 'text-white/80' : 'text-muted-foreground',
      )}
      title={noDataReason}
    >
      —
    </td>
  );
}

function MatrixCell({
  value,
  tone,
  format,
  org,
  noDataReason,
  onActivate,
  hasOnClick,
}: Readonly<CellProps>) {
  if (value === null) return noDataCell(noDataReason, org);

  const bg = org ? orgCellBackgroundClass(tone) : cellBackgroundClass(tone);
  const display = formatValue(value, format);

  // S6847 compliance: only attach interactive behaviour to a real <button>;
  // never put onClick on a <td>. When a callback is supplied we render an
  // unstyled button inside the cell so the keyboard activation comes for free.
  if (hasOnClick && onActivate && !org) {
    return (
      <td className={cn(CELL_BASE_CLASSES, bg)}>
        <button
          type="button"
          onClick={onActivate}
          className="w-full h-full bg-transparent p-0 m-0 font-semibold tabular-nums cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        >
          {display}
        </button>
      </td>
    );
  }

  return (
    <td className={cn(CELL_BASE_CLASSES, bg, org && 'text-white')}>{display}</td>
  );
}

interface TeamCellProps {
  name: string;
  org: boolean;
}

function TeamCell({ name, org }: Readonly<TeamCellProps>) {
  return (
    <td
      className={cn(
        'px-3.5 py-2 text-left font-semibold border-b border-r border-[var(--color-border)] min-w-[130px]',
        org ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
      )}
    >
      {name}
    </td>
  );
}

interface TrendCellProps {
  values: (number | null)[];
  tone: MatrixCellTone;
  org: boolean;
}

function TrendCell({ values, tone, org }: Readonly<TrendCellProps>) {
  const hasAll = values.length > 0 && values.every((v) => v !== null);
  return (
    <td
      className={cn(
        'border-b border-r border-[var(--color-border)] last:border-r-0 p-1',
        org ? 'bg-primary' : 'bg-background',
      )}
    >
      {hasAll ? (
        <MatrixSparkline
          values={values}
          stroke={org ? '#ffffff' : sparklineStroke(tone)}
        />
      ) : (
        <span className={cn('block text-center', org ? 'text-white/80' : 'text-muted-foreground')}>—</span>
      )}
    </td>
  );
}

interface DeltaCellProps {
  delta: DeltaDescriptor;
  org: boolean;
}

function DeltaCell({ delta, org }: Readonly<DeltaCellProps>) {
  return (
    <td
      className={cn(
        'px-1.5 py-2 text-center border-b border-r border-[var(--color-border)] last:border-r-0',
        org ? 'bg-primary' : 'bg-background',
      )}
    >
      <MatrixDelta delta={delta} org={org} />
    </td>
  );
}

interface RowProps {
  row: IMatrixRow;
  org: boolean;
  spec: IMatrixSpec;
}

function buildCellTones(row: IMatrixRow, spec: IMatrixSpec): MatrixCellTone[] {
  return row.values.map((v) => classifyCellTone(v, spec.thresholds));
}

function MatrixRow({ row, org, spec }: Readonly<RowProps>) {
  const tones = buildCellTones(row, spec);
  const lastIndex = row.values.length - 1;
  const trendTone: MatrixCellTone = tones[lastIndex] ?? null;
  const lowerBetter = spec.thresholds.direction === 'lower_better';
  const delta = describeDelta(
    row.values[lastIndex - 1] ?? null,
    row.values[lastIndex] ?? null,
    lowerBetter,
    spec.format,
  );
  const hasOnClick = Boolean(spec.onCellClick);

  return (
    <tr className={org ? 'org' : undefined} data-row-id={row.id} data-org={org || undefined}>
      <TeamCell name={row.name} org={org} />
      {row.values.map((value, columnIndex) => (
        <MatrixCell
          key={`${row.id}-c${columnIndex}`}
          value={value}
          tone={tones[columnIndex]}
          format={spec.format}
          org={org}
          noDataReason={value === null ? row.noDataReason : undefined}
          hasOnClick={hasOnClick}
          onActivate={() => spec.onCellClick?.({ rowId: row.id, columnIndex, value })}
        />
      ))}
      <TrendCell values={row.values} tone={trendTone} org={org} />
      <DeltaCell delta={delta} org={org} />
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Header row
// ---------------------------------------------------------------------------

interface HeaderProps {
  columnLabels: string[];
  onSortByColumn?: (columnIndex: number) => void;
}

function MatrixHeader({ columnLabels, onSortByColumn }: Readonly<HeaderProps>) {
  return (
    <thead>
      <tr>
        <th className="bg-muted text-left text-xs font-bold uppercase tracking-wider px-3.5 py-2 border-b border-r border-[var(--color-border)] text-secondary-700 min-w-[130px]">
          Team
        </th>
        {columnLabels.map((label, columnIndex) => (
          <th
            key={`col-${label}-${columnIndex}`}
            className="bg-muted text-center text-xs font-bold uppercase tracking-wider px-1.5 py-2 border-b border-r border-[var(--color-border)] last:border-r-0 text-secondary-700"
          >
            {onSortByColumn ? (
              <button
                type="button"
                onClick={() => onSortByColumn(columnIndex)}
                className="font-bold uppercase tracking-wider text-xs cursor-pointer bg-transparent p-0 m-0 focus:outline-none focus-visible:underline hover:underline"
              >
                {label}
              </button>
            ) : (
              label
            )}
          </th>
        ))}
        <th className="bg-muted text-center text-xs font-bold uppercase tracking-wider px-1.5 py-2 border-b border-r border-[var(--color-border)] text-secondary-700">
          Trend
        </th>
        <th className="bg-muted text-center text-xs font-bold uppercase tracking-wider px-1.5 py-2 border-b border-[var(--color-border)] text-secondary-700">
          Δ vs last
        </th>
      </tr>
    </thead>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface MatrixProps {
  spec: IMatrixSpec;
  /** Optional className spread onto the wrapping div. */
  className?: string;
}

export function Matrix({ spec, className }: Readonly<MatrixProps>) {
  const lowerBetter = spec.thresholds.direction === 'lower_better';
  const sortedRows = sortRowsByLatest(spec.rows, lowerBetter);

  return (
    <div className={cn('w-full overflow-x-auto', className)} data-testid="matrix">
      <table className="w-full border-collapse text-xs border border-[var(--color-border)] rounded-md overflow-hidden">
        <MatrixHeader columnLabels={spec.columnLabels} onSortByColumn={spec.onSortByColumn} />
        <tbody>
          {spec.orgRow && <MatrixRow row={spec.orgRow} org spec={spec} />}
          {sortedRows.map((row) => (
            <MatrixRow key={row.id} row={row} org={false} spec={spec} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
